'use client';

import { RESPONSIVE_WIDTHS } from '@/lib/image-widths.mjs';
import { buildPublicUrl } from '@/lib/r2-urls';
import {
    buildResponsiveSrcSet,
    getExtension,
    isCdnUrl,
    stripExtension,
} from '@/lib/responsive-image';

function resolveSrcSet(src, responsive, srcSetExt) {
    if (!responsive || !isCdnUrl(src)) return undefined;
    const ext =
        srcSetExt && typeof srcSetExt === 'string'
            ? srcSetExt.replace(/^\./, '')
            : getExtension(src);
    if (!ext || ext === 'svg' || /-\d+w\.\w+$/.test(src)) return undefined;
    return buildResponsiveSrcSet(stripExtension(src), ext);
}

/**
 * Renders a source image cropped to a normalised rect.
 *
 * `crop` uses the same percentage shape react-easy-crop reports from
 * `onCropComplete` (`{ x, y, width, height }`, 0–100 relative to the source),
 * so a rect captured in the admin can be applied here without conversion.
 *
 * The crop is applied with CSS rather than by baking a new file: the source is
 * stored once and every context (card, hero) reads its own window into it, so
 * re-framing never needs a re-upload.
 */
export default function CroppedImage({
    src,
    alt,
    crop = null,
    aspect,
    className = '',
    sizes,
    priority = false,
    responsive = true,
    srcSetExt,
    objectPosition,
    skipCrop = false,
}) {
    if (!src) return null;

    // Strip query strings before constructing variant URLs (srcSet, blur).
    // buildPublicUrl preserves them for cache-busting, but CDN variant files
    // (e.g. photo-400w.webp) are distinct objects without query parameters.
    const resolvedSrc = buildPublicUrl(src).split('?')[0];
    const srcSet = resolveSrcSet(resolvedSrc, responsive, srcSetExt);
    const hasCrop =
        !skipCrop &&
        crop &&
        Number.isFinite(crop.width) &&
        Number.isFinite(crop.height) &&
        crop.width > 0 &&
        crop.height > 0;

    const imageStyle = hasCrop
        ? {
              position: 'absolute',
              width: `${10000 / crop.width}%`,
              height: `${10000 / crop.height}%`,
              left: `${(-crop.x / crop.width) * 100}%`,
              top: `${(-crop.y / crop.height) * 100}%`,
              maxWidth: 'none',
              // The crop box is sized to the source aspect, so `cover` matches
              // `fill` when the stored rect lines up with the wrapper aspect. It
              // also stops a mismatched rect from stretching the image.
              objectFit: 'cover',
          }
        : {
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              ...(objectPosition ? { objectPosition } : {}),
          };

    const blurSrc =
        srcSet && responsive
            ? resolvedSrc.replace(
                  /\.[a-zA-Z0-9]+$/,
                  `-${RESPONSIVE_WIDTHS[0]}w.webp`,
              )
            : undefined;

    return (
        <div
            className={`relative overflow-hidden ${className}`}
            style={aspect ? { aspectRatio: aspect } : undefined}
        >
            {blurSrc && (
                <img
                    src={blurSrc}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover scale-110 blur-lg"
                    loading="eager"
                    decoding="async"
                />
            )}
            <img
                src={resolvedSrc}
                srcSet={srcSet}
                sizes={srcSet ? sizes || '100vw' : undefined}
                alt={alt || ''}
                style={imageStyle}
                loading={priority ? 'eager' : 'lazy'}
                fetchPriority={priority ? 'high' : undefined}
                decoding="async"
            />
        </div>
    );
}
